import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as d3 from 'd3';
import { Globe, Tag, Info, Database, Search, Shield, Radio, Clock, ExternalLink, Copy, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { AssetDetailTabs } from '../../components/assets/AssetDetailTabs';
import { AssetGraph } from '../../components/graphs/AssetGraph';
import { TechnologyStack } from '../../components/assets/TechnologyStack';
import { AssetKeyInfo } from '../../components/assets/detail/AssetKeyInfo';
import { AssetDNSRecords } from '../../components/assets/detail/AssetDNSRecords';
import { AssetServices } from '../../components/assets/detail/AssetServices';
import { AssetTLSCertificate } from '../../components/assets/detail/AssetTLSCertificate';
import { AssetRecentFindings } from '../../components/assets/detail/AssetRecentFindings';
import { AssetPorts } from '../../components/assets/detail/AssetPorts';
import { AssetFindings } from '../../components/assets/detail/AssetFindings';
import { AssetEvents } from '../../components/assets/detail/AssetEvents';
import { AssetNotes } from '../../components/assets/detail/AssetNotes';
import { AssetRiskScoreCard } from '../../components/assets/detail/AssetRiskScoreCard';
import { AssetsApi, Subdomain, Port, FindingsApi, TechnologiesApi, Finding, Technology as ApiTechnology, PaginatedPortList } from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';

// API will be initialized with auth token

interface SubdomainData {
  subdomain: Subdomain | null;
  findings: {
    results: Finding[];
    count: number;
    next: string | null;
    previous: string | null;
  };
  technologies: ApiTechnology[] | null;
  ports: Port[] | null;
  loading: boolean;
  error: string | null;
}

const initialSubdomainData: SubdomainData = {
  subdomain: null,
  findings: {
    results: [],
    count: 0,
    next: null,
    previous: null
  },
  technologies: null,
  ports: null,
  loading: true,
  error: null
};

// Define proper types for graph data - using the same types as AssetGraph component
interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  type: string;
  risk: string;
}

interface GraphLink {
  source: string;
  target: string;
  type: string;
}

const generateAssetGraphData = (subdomain: Subdomain | null, data: SubdomainData) => {
  if (!subdomain) return { nodes: [] as GraphNode[], links: [] as GraphLink[] };
  
  const nodes: GraphNode[] = [
    { id: 'subdomain', label: subdomain.subdomainName || '', type: 'subdomain', risk: subdomain.riskScore || 'low' }
  ];
  
  const links: GraphLink[] = [];

  // Add IP nodes and edges
  if (subdomain.ipAddresses) {
    subdomain.ipAddresses.forEach((ipAddress, index) => {
      const ipId = `ip_${index}`;
      nodes.push({
        id: ipId,
        label: ipAddress?.toString() || '',
        type: 'ip',
        risk: 'low'
      });
      links.push({ source: 'subdomain', target: ipId, type: 'resolves_to' });
    });
  }

  // Add port nodes and edges from real data
  if (data.ports) {
    data.ports.forEach((port, index) => {
      const portId = `port_${index}`;
      nodes.push({
        id: portId,
        label: `Port ${port.number} (${port.service || 'Unknown'})`,
        type: 'port',
        risk: 'low'
      });
      // Connect ports to the first IP if available, otherwise to subdomain
      const sourceId = nodes.find(n => n.type === 'ip')?.id || 'subdomain';
      console.log(sourceId)
      links.push({ source: sourceId, target: portId, type: 'exposes' });
    });
  }

  // Add findings as vulnerability nodes
  if (data.findings?.results) {
    data.findings.results.forEach((finding, index) => {
      const findingId = `finding_${index}`;
      nodes.push({
        id: findingId,
        label: finding.title || 'Unknown Finding',
        type: 'vulnerability',
        risk: finding.severity || 'low'
      });
      // Connect findings to domain
      links.push({ source: 'subdomain', target: findingId, type: 'vulnerable_to' });
    });
  }

  // Add technologies
  if (data.technologies) {
    data.technologies.forEach((tech, index) => {
      const techId = `tech_${index}`;
      nodes.push({
        id: techId,
        label: tech.name || 'Unknown Technology',
        type: 'service',
        risk: 'low'
      });
      links.push({ source: 'subdomain', target: techId, type: 'uses' });
    });
  }

  return { nodes, links };
};

// Define interfaces for parsed JSON data
interface ParentDomain {
  domain_name?: string;
  id?: number;
  [key: string]: any;
}

interface FindingsStats {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  [key: string]: any;
}

interface TechnologyData {
  name: string;
  version?: string | null;
  [key: string]: any;
}

// Helper function to safely get the subdomain ID as a string
const safeGetId = (id: number | null | undefined): string => {
  return id?.toString() || '';  // Return empty string as fallback
};

// Helper function to safely access nested properties
const safeAccess = <T, K extends keyof T>(obj: T | null | undefined, key: K): T[K] | undefined => {
  return obj ? obj[key] : undefined;
};

// Helper function to parse JSON data safely
const parseJsonData = <T extends object>(data: string | null | undefined): T | null => {
  if (!data) return null;
  try {
    // Parse string or return as is if already an object
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    // Ensure the result is treated as the expected type
    return parsed as T;
  } catch (e) {
    console.error('Error parsing data:', e);
    return null;
  }
};

const SubdomainDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState<SubdomainData>(initialSubdomainData);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchSubdomainData = async () => {
      if (!id || !currentTeam?.slug || !token) return;

      try {
        const assetsApi = new AssetsApi(getApiConfiguration(token));
        const technologiesApi = new TechnologiesApi(getApiConfiguration(token));

        const [subdomainResponse, portsResponse, technologiesResponse] = await Promise.all([
          assetsApi.subdomainsRetrieve({ id, teamSlug: currentTeam.slug }),
          assetsApi.assetsPortsRetrieve({ assetId: parseInt(id), assetType: 'subdomain', teamSlug: currentTeam.slug }),
          technologiesApi.technologiesAssetsList({ assetId: parseInt(id), assetType: 'subdomain', teamSlug: currentTeam.slug })
        ]);

        setData(prev => ({
          subdomain: subdomainResponse,
          ports: portsResponse ? (portsResponse as unknown as PaginatedPortList).results : null, // Wrap single port in array
          findings: prev.findings, // Preserve existing findings
          technologies: technologiesResponse,
          loading: false,
          error: null
        }));
      } catch (error) {
        console.error('Error fetching subdomain data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to fetch subdomain data'
        }));
      }
    };

    fetchSubdomainData();
  }, [id, currentTeam?.slug, token]);

  // Separate effect for fetching findings
  const [findingsFilters, setFindingsFilters] = useState({
    search: '',
    severity: undefined as string | undefined,
    status: undefined as string | undefined,
    createdLast: undefined as number | undefined
  });

  useEffect(() => {
    const fetchFindings = async () => {
      if (!id || !currentTeam?.slug || !token) return;

      try {
        const findingsApi = new FindingsApi(getApiConfiguration(token));
        const findingsResponse = await findingsApi.assetFindingsList({ 
          assetId: parseInt(id), 
          assetType: 'subdomain', 
          teamSlug: currentTeam.slug,
          page: currentPage,
          ...findingsFilters
        });

        setData(prev => ({
          ...prev,
          findings: {
            results: findingsResponse.results,
            count: findingsResponse.count,
            next: findingsResponse.next || null,
            previous: findingsResponse.previous || null
          }
        }));
      } catch (error) {
        console.error('Error fetching findings:', error);
      }
    };

    fetchFindings();
  }, [id, currentTeam?.slug, token, currentPage, findingsFilters]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleFilterChange = useCallback((filters: {
    search?: string;
    severity?: string;
    status?: string;
    createdLast?: number;
  }) => {
    setFindingsFilters(prevFilters => ({
      ...prevFilters,
      ...filters
    }));
  }, []);

  if (data.loading) {
    return <div>Loading...</div>;
  }

  if (data.error || !data.subdomain) {
    return <div>Error: {data.error || 'Subdomain not found'}</div>;
  }

  const subdomain = data.subdomain;
  const ports = subdomain.ports;
  const { results: findings, next: findingsNext, count: findingsCount, previous: findingsPrevious } = data.findings;
  const technologies = data.technologies;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              {/* Main Content - 2 columns */}
              <div className="col-span-2 space-y-6">
                <AssetDNSRecords records={subdomain.dnsRecords || []} />
                <AssetServices services={subdomain.ports || []} />
                <AssetRecentFindings findings={findings.slice(0, 3)} />
              </div>

              {/* Sidebar - 1 column */}
              <div className="space-y-6">
                <AssetTLSCertificate certificate={subdomain.tlsCertificate || {}} />
              </div>
            </div>
          </div>
        );
      case 'graph':
        return (
          <div className="space-y-6">
            <div className="bg-[#0F1629] rounded-xl border border-gray-800/50">
              {/* Graph Controls */}
              <div className="p-4 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Button variant="outline" size="sm">
                      <Maximize2 className="h-4 w-4 mr-2" />
                      Zoom to Fit
                    </Button>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-400">Layout:</label>
                      <select className="bg-gray-800/30 border border-gray-800 rounded-lg text-sm text-white px-2 py-1">
                        <option>Force-Directed</option>
                        <option>Hierarchical</option>
                        <option>Circular</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="text-xs text-gray-400">Critical</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-orange-500" />
                      <span className="text-xs text-gray-400">High</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-yellow-500" />
                      <span className="text-xs text-gray-400">Medium</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="h-3 w-3 rounded-full bg-emerald-500" />
                      <span className="text-xs text-gray-400">Low</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph Canvas */}
              <div className="p-6">
                <div className="w-full h-[600px] rounded-lg bg-[#0F1629]">
                  <AssetGraph 
                    nodes={generateAssetGraphData(subdomain, data).nodes}
                    links={generateAssetGraphData(subdomain, data).links}
                    width={800}
                    height={600}
                  />
                </div>
              </div>
            </div>

            {/* Graph Statistics */}
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Graph Statistics</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Nodes</span>
                    <span className="text-sm text-white">{generateAssetGraphData(subdomain, data).nodes.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Total Edges</span>
                    <span className="text-sm text-white">{generateAssetGraphData(subdomain, data).links.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <span className="text-sm text-gray-400">Connected Components</span>
                    <span className="text-sm text-white">1</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Node Distribution</h3>
                <div className="space-y-4">
                  {['domain', 'ip', 'port', 'service', 'vulnerability', 'certificate'].map(type => (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <span className="text-sm text-gray-400 capitalize">{type}s</span>
                      <span className="text-sm text-white">
                        {generateAssetGraphData(subdomain, data).nodes.filter(n => n.type === type).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h3 className="text-lg font-semibold text-white mb-4">Risk Distribution</h3>
                <div className="space-y-4">
                  {['critical', 'high', 'medium', 'low'].map(risk => (
                    <div key={risk} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className={`h-3 w-3 rounded-full ${
                          risk === 'critical' ? 'bg-red-500' :
                          risk === 'high' ? 'bg-orange-500' :
                          risk === 'medium' ? 'bg-yellow-500' :
                          'bg-emerald-500'
                        }`} />
                        <span className="text-sm text-gray-400 capitalize">{risk}</span>
                      </div>
                      <span className="text-sm text-white">
                        {generateAssetGraphData(subdomain, data).nodes.filter(n => n.risk === risk).length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'ports':
        return <AssetPorts ports={typeof subdomain.ports === 'object' ? subdomain.ports : []} />;
      case 'findings':
        return (
          <AssetFindings 
            findings={findings} 
            paginationInfo={{
              count: findingsCount,
              next: findingsNext,
              previous: findingsPrevious
            }}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            itemsPerPage={10}
            onFilterChange={handleFilterChange}
          />
        );
      case 'events':
        return <AssetEvents entries={subdomain.history} />;
      case 'notes':
        // Only render notes if we have a valid ID
        return subdomain?.id ? (
          <AssetNotes 
            objectId={subdomain.id.toString()} 
            objectType="subdomain" 
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Globe className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">{subdomain.subdomainName}</h1>
                <Badge>Domain: {parseJsonData<ParentDomain>(subdomain.parentDomain)?.domain_name || 'Unknown'}</Badge>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Tag className="h-4 w-4" />
                  <div className="flex items-center space-x-2">
                    {subdomain.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full bg-gray-800/50 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span>Last scan: {new Date(subdomain.createdAt).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
              <Copy className="h-4 w-4" />
            </button>
            <a
              href={`https://${subdomain.subdomainName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4 mt-6">
          <AssetRiskScoreCard riskScore={subdomain?.riskScore?.toUpperCase() || 'Unknown'} findings={subdomain.findingsStats} />
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Open Ports</div>
              <Radio className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{subdomain.ports.length}</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Active Findings</div>
              <Shield className="h-4 w-4 text-red-500" />
            </div>
            <div className="text-lg font-medium text-white mt-1">{parseJsonData<FindingsStats>(subdomain.findingsStats)?.total || 0}</div>
          </div>
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">Technologies</div>
              <Shield className="h-4 w-4 text-red-500" />
            </div>
            <br></br>
            <TechnologyStack
                    technologies={parseJsonData<TechnologyData[]>(subdomain.technologies) || []}
                    maxVisible={5}
                  />
          </div>
          
 
        </div>
      </div>

      {/* Tabs */}
      {subdomain && (
        <AssetDetailTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          assetId={safeGetId(subdomain.id)}
          portsCount={data.ports?.length || 0}
          findingsCount={parseJsonData<FindingsStats>(subdomain.findingsStats)?.total || 0}
          eventsCount={subdomain.history?.length || 0}
        />
      )}

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default SubdomainDetail;